import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  duration?: number; // milliseconds, 0 = no auto-dismiss
}

interface ToastProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toasts, onDismiss }) => {
  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle style={{ width: '20px', height: '20px', color: '#10b981' }} />;
      case 'error':
        return <AlertCircle style={{ width: '20px', height: '20px', color: '#ef4444' }} />;
      case 'warning':
        return <AlertCircle style={{ width: '20px', height: '20px', color: '#f59e0b' }} />;
      case 'info':
        return <Info style={{ width: '20px', height: '20px', color: '#3b82f6' }} />;
    }
  };

  const getBackgroundColor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return '#f0fdf4';
      case 'error':
        return '#fef2f2';
      case 'warning':
        return '#fffbeb';
      case 'info':
        return '#f0f9ff';
    }
  };

  const getBorderColor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return '#d1fae5';
      case 'error':
        return '#fee2e2';
      case 'warning':
        return '#fef3c7';
      case 'info':
        return '#e0f2fe';
    }
  };

  const getTextColor = (type: ToastType) => {
    switch (type) {
      case 'success':
        return '#065f46';
      case 'error':
        return '#7f1d1d';
      case 'warning':
        return '#78350f';
      case 'info':
        return '#082f49';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '24px',
        right: '24px',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={onDismiss}
          icon={getIcon(toast.type)}
          backgroundColor={getBackgroundColor(toast.type)}
          borderColor={getBorderColor(toast.type)}
          textColor={getTextColor(toast.type)}
        />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
  icon: React.ReactNode;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
}

const ToastItem: React.FC<ToastItemProps> = ({
  toast,
  onDismiss,
  icon,
  backgroundColor,
  borderColor,
  textColor,
}) => {
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        onDismiss(toast.id);
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast, onDismiss]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '8px',
        minWidth: '300px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        pointerEvents: 'auto',
      }}
    >
      {icon}
      <span style={{ flex: 1, fontSize: '14px', color: textColor, fontWeight: '500' }}>
        {toast.message}
      </span>
      <button
        onClick={() => onDismiss(toast.id)}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          color: textColor,
        }}
      >
        <X style={{ width: '16px', height: '16px' }} />
      </button>
    </div>
  );
};

// Hook for managing toasts
export function useToast() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = (type: ToastType, message: string, duration: number = 5000) => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, type, message, duration }]);
    return id;
  };

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const success = (message: string, duration?: number) => addToast('success', message, duration);
  const error = (message: string, duration?: number) => addToast('error', message, duration);
  const warning = (message: string, duration?: number) => addToast('warning', message, duration);
  const info = (message: string, duration?: number) => addToast('info', message, duration);

  return {
    toasts,
    addToast,
    dismissToast,
    success,
    error,
    warning,
    info,
  };
}

export default Toast;
